import type {IDoppeViewerActionContext} from './doppe-viewer-action-context';
import {
    DoppeActionGoalType,
    doppeActionTypeSupportsOpeningListItemWidgetAsLink,
    WidgetRenderingContextType
} from '../../client-server-common/doppe-action-types/doppe-action-type';
import type {DoppeHideableValue, DoppeListActionSettings} from '../../client-server-common';
import {
    DoppeActionListItemClickBehaviour,
    doppeHideableValueIsVisibleAndNotEqualValue
} from '../../client-server-common';
import {DoppeActionWidgetPopupMode} from '../../client-server-common/types/doppe-action-widget-popup-mode';
import {
    DoppeViewerListItemInterstitialCouponModalPanel
} from '../../components/doppe-viewer-list-item-interstitial-coupon-modal-panel/doppe-viewer-list-item-interstitial-coupon-modal-panel';
import React from 'react';
import type { ObservableValue} from '@wix/devzai-utils-common';
import {identity} from '@wix/devzai-utils-common';
import {BymoPageViewerMode} from '../../components/bymo-page-viewer-context/bymo-page-viewer-context';
import {
    DoppeActionCheckoutModal
} from '../../doppe-action-types/doppe-actions-common/doppe-action-checkout-view/doppe-action-checkout-modal';
import {
    DoppeActionThankYouModal
} from '../../doppe-action-types/doppe-actions-common/doppe-action-thank-you-view/thankyou-view-modal';
import type {
    DoppeActionCheckoutViewSettings
} from '../../doppe-action-types/doppe-actions-common/doppe-action-checkout-view/doppe-action-checkout-view';
import type {DoppeDtoCheckoutIntent} from '../../client-server-common/types/doppe-dto-checkout-intent';
import type {DoppeViewerLocalesAccessor} from '../../client/doppe-viewer-locales/doppe-viewer-locales';
import {DoppeViewerModalPanelLayout} from '../../components/doppe-viewer-modal-panel/doppe-viewer-modal-panel';
import {BymoPageLayout} from '../../components/bymo-page-style/bymo-page-style';
import type {
    DoppeViewerModalContent
} from '../../components/doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import {
    DoppeViewerModalOpeningAnimation,
} from '../../components/doppe-viewer-modals-controller/doppe-viewer-modals-controller';
import {DoppeViewerContentModal} from '../../components/doppe-viewer-content-modal/doppe-viewer-content-modal';
import {
    DoppeActionModalIframeContent
} from '../../components/doppe-action-modal-iframe-content/doppe-action-modal-iframe-content';
import {WixEcomCheckoutCustomLineItem, WixEcomCheckoutCustomLineItemKind} from '@wix/devzai-common-wix';
import type {DoppeViewerLang} from '../../client-server-common/doppe-viewer/doppe-viewer-lang';

export function doppeViewerActionScrollToActionWhenInActionsList(
    actionContext: IDoppeViewerActionContext,
    options: {
        animated?: boolean;
        scrollOnlyWhenNotInViewport?: boolean;
    } = {}
) {
    const {
        animated = false,
        scrollOnlyWhenNotInViewport = false
    } = options;

    const actionsListController = actionContext.actionsListController;

    if (actionsListController) {
        actionsListController.scrollToAction(actionContext.action.id, {
            animated: animated,
            scrollOnlyWhenNotInViewport: scrollOnlyWhenNotInViewport
        }).catch(console.error);
    }
}

export function doppeViewerActionSetIsRenderingInnerState(
    actionContext: IDoppeViewerActionContext,
    isRenderingInnerState: boolean
) {
    actionContext.observableIsRenderingInnerState.setValue(isRenderingInnerState);

    doppeViewerActionScrollToActionWhenInActionsList(actionContext, {
        animated: false,
        scrollOnlyWhenNotInViewport: true
    });
}

export function doppeViewerActionIsRenderedInPage(
    actionContext: IDoppeViewerActionContext
) {
    const widgetRenderingContextType = actionContext.getWidgetRenderingContextType();

    return widgetRenderingContextType === WidgetRenderingContextType.Card || widgetRenderingContextType === WidgetRenderingContextType.Strip;
}

export function doppeViewerActionResolveListItemClickBehaviour<T extends DoppeListActionSettings.WithItemClickBehaviour>(
    actionContext: IDoppeViewerActionContext<T>
) {

    const actionType = actionContext.getActionType();
    doppeActionTypeSupportsOpeningListItemWidgetAsLink(actionType);

    const isRenderedInPage = doppeViewerActionIsRenderedInPage(actionContext);
    const itemClickBehaviour = actionContext.action.settings.itemClickBehaviour;

    const normalizedItemClickBehaviour =
        actionType.forceFullScreenOnListItemClick ? DoppeActionListItemClickBehaviour.PreviewItemAsPopup :
        !isRenderedInPage && itemClickBehaviour === DoppeActionListItemClickBehaviour.PreviewItemAsPopup ?
        DoppeActionListItemClickBehaviour.PreviewItem :
        itemClickBehaviour;

    return normalizedItemClickBehaviour === DoppeActionListItemClickBehaviour.OpenLink && !doppeActionTypeSupportsOpeningListItemWidgetAsLink(actionContext.getActionType()) ?
        DoppeActionListItemClickBehaviour.PreviewItem :
        normalizedItemClickBehaviour;
}

export function doppeViewerActionHandleListItemClick<T extends DoppeListActionSettings.WithItemClickBehaviour>(
    actionContext: IDoppeViewerActionContext<T>,
    options: {
        itemId: string;
        itemIndex: number;
        itemLink?: string;
        couponCode?: DoppeHideableValue<string>;
        customItemClickBehaviour?: (itemClickBehaviour: DoppeActionListItemClickBehaviour) => DoppeActionListItemClickBehaviour;
        widgetPopupMode?: DoppeActionWidgetPopupMode;
        showHeader?: boolean;
        openLinkInPopup?: boolean;
    }
) {
    const {
        itemId,
        itemIndex,
        itemLink,
        couponCode,
        customItemClickBehaviour = identity,
        widgetPopupMode,
        showHeader = true,
        openLinkInPopup = false
    } = options;

    const observableCurrentItemIdToRender = actionContext.observableCurrentItemIdToRender;

    const itemClickBehaviour = customItemClickBehaviour(doppeViewerActionResolveListItemClickBehaviour(actionContext));

    switch (itemClickBehaviour) {
        case DoppeActionListItemClickBehaviour.PreviewItem: {
            doppeViewerActionSetIsRenderingInnerState(actionContext, true);
            observableCurrentItemIdToRender?.setValue(itemId);

            actionContext.reportInnerItemPreviewed({
                innerItemId: itemId,
                innerItemPosition: itemIndex,
                previewType: DoppeActionListItemClickBehaviour.PreviewItem
            });

            break;
        }
        case DoppeActionListItemClickBehaviour.PreviewItemAsPopup: {
            actionContext.previewAction(actionContext.action.id, {
                listItemId: itemId,
                widgetPopupMode: widgetPopupMode ?? DoppeActionWidgetPopupMode.Drawer,
                actionIndex: actionContext.getActionIndex(),
                startNewContext: true,
                showHeader: showHeader,
            })

            actionContext.reportInnerItemPreviewed({
                innerItemId: itemId,
                innerItemPosition: itemIndex,
                previewType: DoppeActionListItemClickBehaviour.PreviewItemAsPopup
            });

            break;
        }
        case DoppeActionListItemClickBehaviour.OpenLink: {
            if (itemLink) {
                if (couponCode && doppeHideableValueIsVisibleAndNotEqualValue(couponCode, '')) {
                    actionContext.modalsController.showInnerModal({
                        content: (
                            <DoppeViewerListItemInterstitialCouponModalPanel
                                couponCode={couponCode}
                                itemLink={itemLink}
                                actionContext={actionContext}
                                itemId={itemId}
                                itemIndex={itemIndex}
                            />
                        )
                    })
                } else {
                    actionContext.reportGoalAchieved({
                        goalType: DoppeActionGoalType.Click,
                        innerItemId: itemId,
                        innerItemPosition: itemIndex

                    });

                    if (openLinkInPopup) {
                        doppeViewerActionContextOpenModal(
                            actionContext,
                            () => {
                                return {
                                    render: () =>
                                        <DoppeActionModalIframeContent
                                            withMinHeight={true}
                                            src={itemLink}
                                        />
                                    ,
                                    dependencies: []
                                }
                            },
                            {
                                mobileLayout:  DoppeViewerModalPanelLayout.FullScreen,
                                desktopLayout: DoppeViewerModalPanelLayout.FullScreen ,
                                showHeader: true,
                                showHeaderAsCloseButton: true,
                                title: ''
                            }
                        );
                    }
                    else {
                        actionContext.navigateToUrl(itemLink, true);
                    }
                }
            }

            break;
        }
    }
}


export function doppeViewerActionOpenCheckoutFlow(options: {
    actionContext: IDoppeViewerActionContext<any>,
    observableCheckoutDetails: ObservableValue<DoppeActionCheckoutViewSettings>,
    innerItemId?: string,
    onCheckoutCompletion: (options: {
        paymentIntentId: string,
        consent?: boolean,
        message?: string
    }) => void;
    isCustomAmount?: boolean;
    ignoreNotify?: boolean;
    viewerLocales?: DoppeViewerLocalesAccessor;
}) {

    const {
        actionContext,
        observableCheckoutDetails,
        innerItemId,
        onCheckoutCompletion,
        isCustomAmount = false,
        ignoreNotify = true,
    } = options;

    void actionContext.performContextCallback(async options => {

        const {
            viewerContextData,
            clientServices
        } = options;

        const checkoutIntent = observableCheckoutDetails.getValue().checkoutIntent;

        const priceData = checkoutIntent.price;

        const createStripePaymentIntent = async () => {
            if (viewerContextData.mode !== BymoPageViewerMode.Live) {
                return {
                    clientSecret: 'demo_client_secret',
                    paymentIntentId: 'demo_payment_intent_id'
                };
            }

            const result = await actionContext.createStripePaymentIntent({
                checkoutIntent: checkoutIntent,
                innerItemId: innerItemId,
            });

            clientServices.viewerAnalyticsClient.logEvent('uouCreatePaymentIntent', {
                ...actionContext.getBiIdFields(),
                value: `${priceData.amount}`,
                currency: priceData.currencyCode,
                noteAdded: false,
                isCustomAmount: isCustomAmount,
                paymentIntentId: result.paymentIntentId,
                innerPageId: actionContext.getCurrentInnerPageId(),
            });

            return result;
        }

        const saveCheckoutIntent = async (options: {
            uouCheckoutData: DoppeDtoCheckoutIntent.UouCheckoutData,
            paymentIntentId: string
        }) => {
            if (viewerContextData.mode !== BymoPageViewerMode.Live) {
                return Promise.resolve();
            }

            const checkoutIntent = observableCheckoutDetails.getValue().checkoutIntent;

            return await actionContext.saveCheckoutIntent({
                checkoutIntent: checkoutIntent,
                uouCheckoutData: options.uouCheckoutData,
                paymentIntentId: options.paymentIntentId,
                innerItemId: innerItemId,
            });
        };

        actionContext.modalsController.showInnerModal({
            content: ({modalController}) => (
                <DoppeActionCheckoutModal
                    dismissButtonCallback={() => {
                        modalController.closeWindow();
                    }}
                    onDone={({
                                 name,
                                 paymentIntentId,
                                 consent,
                                 message
                             }) => {

                        if (!ignoreNotify) {
                            clientServices
                                .getApiClient()
                                .notifyPaymentSuccess({
                                    paymentIntentId: paymentIntentId,
                                    uouMessage: message,
                                    emailSubscriptionConsent: consent,
                                })
                                .catch(console.error);
                        }


                        modalController.closeWindowImmediately();
                        onCheckoutCompletion?.({
                            paymentIntentId: paymentIntentId,
                            consent: consent,
                            message: message
                        });

                        const checkoutViewSettings = observableCheckoutDetails.getValue();
                        actionContext.modalsController.showInnerModal({
                            content: ({modalController}) => (
                                <DoppeActionThankYouModal
                                    dismissButtonCallback={() => {
                                        modalController.closeWindow();
                                    }}
                                    onConfirm={() => {
                                        modalController.closeWindow();
                                    }}
                                    name={name}
                                    price={checkoutViewSettings.checkoutIntent.price}
                                    customThankYouMessage={checkoutViewSettings.checkoutIntent.checkoutSettings.customThankYouMessage}
                                />
                            )
                        })
                    }}
                    actionContext={actionContext}
                    observableCheckoutDetails={observableCheckoutDetails}
                    createStripePaymentIntent={createStripePaymentIntent}
                    saveCheckoutIntent={saveCheckoutIntent}
                    demoMode={viewerContextData.mode !== BymoPageViewerMode.Live}
                    isCustomAmount={false}
                />
            )
        })

    })
}


export function doppeViewerActionContextOpenModal(
    actionContext: IDoppeViewerActionContext,
    content: DoppeViewerModalContent,
    options?: {
        onClosed?: () => void;
        widgetPopupMode?: DoppeActionWidgetPopupMode;
        withoutOverlay?: boolean;
        showHeader?: boolean;
        showHeaderAsCloseButton?: boolean;
        title?: string;
        mobileLayout?: DoppeViewerModalPanelLayout;
        desktopLayout?: DoppeViewerModalPanelLayout;
    }) {

    const {
        onClosed,
        withoutOverlay,
        showHeader = true,
        showHeaderAsCloseButton = false,
        title = '',
        mobileLayout = DoppeViewerModalPanelLayout.FullScreen,
        desktopLayout = DoppeViewerModalPanelLayout.Inner,
    } = options ?? {};

    actionContext.modalsController.showInnerModal({
        onClosed: onClosed,
        withoutOverlay: withoutOverlay,
        openingAnimation: bymoPageLayout => {
            const modalPanelLayout = bymoPageLayout === BymoPageLayout.Wide ?
                desktopLayout :
                mobileLayout;

            switch (modalPanelLayout) {
                case DoppeViewerModalPanelLayout.Drawer: {
                    return DoppeViewerModalOpeningAnimation.SlideInFromBottom;
                }
                default: {
                    return DoppeViewerModalOpeningAnimation.FadeIn;
                }
            }
        },
        content: ({modalController}) => (
            <DoppeViewerContentModal
                closeCallback={() => {
                    modalController.closeWindow()
                }}
                mobileLayout={mobileLayout}
                desktopLayout={desktopLayout}
                content={content}
                showHeader={showHeader}
                showHeaderAsCloseButton={showHeaderAsCloseButton}
                title={title}
            />
        )
    })
}

/**
 * Opens a URL in an iframe modal using the action context.
 * This is a common pattern for displaying external content in a modal.
 */
export function doppeViewerActionContextOpenUrlInIframeModal(
    actionContext: IDoppeViewerActionContext,
    url: string
) {
    doppeViewerActionContextOpenModal(
        actionContext,
        () => ({
            render: () => (
                <DoppeActionModalIframeContent
                    withMinHeight={false}
                    src={url}
                />
            ),
            dependencies: []
        }),
        {
            mobileLayout: DoppeViewerModalPanelLayout.FullScreen,
            desktopLayout: DoppeViewerModalPanelLayout.FullScreen,
            showHeader: true,
            showHeaderAsCloseButton: true,
            title: ''
        }
    );
}

/**
 * Opens an external URL based on the viewer mode, using the action context for modal display.
 * 
 * In live preview mode: Opens the URL in an iframe modal
 * In live viewer mode: Navigates to the URL in the same tab
 * 
 * This is a convenience wrapper that combines viewerEntityNavigationOpenExternalUrl
 * with doppeViewerActionContextOpenUrlInIframeModal for action context users.
 */
export function doppeViewerActionContextOpenExternalUrl(
    actionContext: IDoppeViewerActionContext,
    url: string,
    options: {
        isLivePreviewMode: boolean;
    }
) {
    if (options.isLivePreviewMode) {
        doppeViewerActionContextOpenUrlInIframeModal(actionContext, url);
    } else {
        window.location.href = url;
    }
}

/**
 * Description line for checkout items - shown in the checkout UI
 */
export interface DoppeWixCheckoutDescriptionLine {
    /** Label for the description line (e.g., "Message", "Note", "Request") */
    label: string;
    /** Value/content of the description line */
    value: string;
}

/**
 * Configuration for a Wix eCommerce checkout item.
 * Used for payment actions like Donate, Paid DM, and Request.
 */
export interface DoppeWixCheckoutLineItem {
    /** Product/service name displayed in checkout */
    productName: string;
    /** Price amount (not in cents, actual amount) */
    price: number;
    /** Description of the item type (e.g., "Donation", "Paid Message", "Custom Request") */
    itemTypeDescription?: string;
    /** Optional quantity - defaults to 1 */
    quantity?: number;
    /** Description lines shown in checkout (for messages, notes, etc.) */
    descriptionLines?: DoppeWixCheckoutDescriptionLine[];
    /** Optional buyer note (displayed in checkout and order) */
    buyerNote?: string;
}

/**
 * Opens Wix eCommerce checkout flow for payment actions.
 * 
 * In live preview mode: Opens checkout in an iframe modal
 * In live viewer mode: Navigates to checkout page in the same tab
 * 
 * This is used by payment actions (Donate, Paid DM, Request) when
 * the checkoutProvider is set to WixEcom.
 */
export async function doppeViewerActionOpenWixCheckoutFlow(
    options: {
        actionContext: IDoppeViewerActionContext<any>;
        lineItem: DoppeWixCheckoutLineItem;
        viewerLang: DoppeViewerLang;
        isLivePreviewMode: boolean;
        modalTitle?: string;
        onCheckoutCompletion?: () => void;
    }
) {
    const {
        actionContext,
        lineItem,
        viewerLang,
        isLivePreviewMode,
        modalTitle = '',
        onCheckoutCompletion,
    } = options;

    await actionContext.performContextCallback(async ({clientServices}) => {
        const customLineItem: WixEcomCheckoutCustomLineItem.Custom = {
            kind: WixEcomCheckoutCustomLineItemKind.Custom,
            productName: lineItem.productName,
            price: String(lineItem.price),
            quantity: lineItem.quantity ?? 1,
            itemTypeDescription: lineItem.itemTypeDescription,
            descriptionLines: lineItem.descriptionLines?.map(line => ({
                name: line.label,
                plainText: line.value,
            })),
        };

        try {
            // Create checkout with Wix eCommerce API
            const checkoutResult = await clientServices.getApiClient().createWixEcomCheckout({
                customLineItems: [customLineItem],
                catalogLineItems: [],
                lang: viewerLang,
                buyerNote: lineItem.buyerNote,
            });

            if (checkoutResult.checkoutPageUrl) {
                if (isLivePreviewMode) {
                    // Open in modal for live preview
                    doppeViewerActionContextOpenModal(
                        actionContext,
                        () => ({
                            render: () => (
                                <DoppeActionModalIframeContent
                                    withMinHeight={false}
                                    src={checkoutResult.checkoutPageUrl}
                                />
                            ),
                            dependencies: []
                        }),
                        {
                            mobileLayout: DoppeViewerModalPanelLayout.FullScreen,
                            desktopLayout: DoppeViewerModalPanelLayout.FullScreen,
                            showHeader: true,
                            showHeaderAsCloseButton: true,
                            title: modalTitle
                        }
                    );
                } else {
                    // Navigate in same tab for live viewer
                    window.location.href = checkoutResult.checkoutPageUrl;
                }

                // Report goal achievement
                // Note: For Wix checkout, we report immediately since we redirect
                // and won't be able to track the actual completion
                onCheckoutCompletion?.();
            }
        } catch (error) {
            console.error('Failed to create Wix checkout:', error);
        }
    });
}
